// ============================================================================
// INBOX API: everything addressed to the signed-in user, invites and shares.
//
// One page answers all of it (frames 1 to 3). The list is `GET /inbox/`; the
// navbar count is a cheaper `GET /inbox/count/`; a focused arrival that is not
// in the list reads `GET /inbox/items/<uuid>/`. Shares are answered by posting
// the row's own save/dismiss path, exactly the way approvals post accept_path.
// ============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { API_BASE, apiClient } from './client'
import type { Approval } from './approvals'

export const INBOX_KEY = ['inbox']
export const INBOX_COUNT_KEY = ['inbox', 'count']
export const INBOX_SENT_KEY = ['inbox', 'sent']
export const INBOX_ITEM_KEY = (uuid: string) => ['inbox', 'item', uuid]

export type ShareKind = 'workout' | 'workout_folder' | 'plan' | 'lens_view' | 'program_view'
export type ShareState = 'pending' | 'saved' | 'dismissed' | 'cancelled'

/** One line item of a folder preview. */
export interface SharePreviewItem {
  name: string
  sport?: string | null
  duration_s?: number | null
}

/** Per-kind and every field optional: the backend sends only what the kind has. */
export interface SharePreview {
  name?: string | null
  sport?: string | null
  duration_s?: number | null
  distance_m?: number | null
  count?: number | null
  weeks?: number | null
  items?: SharePreviewItem[]
}

/** One share delivery. Invites keep the approvals shape, so the two item
 *  families are told apart by `kind === 'share'`. */
export interface ShareItem {
  kind: 'share'
  uuid: string
  share_kind: ShareKind
  title: string
  requested_by: { name: string } | null
  created_at: string | null
  note?: string | null
  state: ShareState
  answered_at?: string | null
  preview?: SharePreview | null
  /** Server-root paths, posted exactly as given. */
  save_path: string
  dismiss_path: string
  open_url?: string | null
}

export type InboxItem = Approval | ShareItem

export interface InboxResponse {
  waiting: InboxItem[]
  earlier: InboxItem[]
  count: number
  /** One source failed on the server, so the list may be short. */
  partial: boolean
  unavailable_kinds: string[]
}

export interface InboxCount {
  count: number
}

export type SentDeliveryStatus = 'waiting' | 'no_account' | 'saved' | 'not_saved' | 'cancelled'

/** One row of `GET /inbox/sent/`, a share the caller sent. */
export interface SentDelivery {
  uuid: string
  share_kind: ShareKind
  item_label: string
  recipient_label: string
  status: SentDeliveryStatus
  created_at: string | null
  resend_path: string
  cancel_path: string
}

export function isShareItem(item: InboxItem): item is ShareItem {
  return item.kind === 'share'
}

export function isInviteItem(item: InboxItem): item is Approval {
  return item.kind !== 'share'
}

export function useInbox() {
  return useQuery({
    queryKey: INBOX_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<InboxResponse>('/inbox/')
      return data
    },
  })
}

/**
 * The navbar count. Polled every 60 s and on window focus, and invalidated by
 * every answer, so the badge never outlives the item it counts.
 */
export function useInboxCount({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: INBOX_COUNT_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<InboxCount>('/inbox/count/')
      return data
    },
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
}

/** A focused share that the list no longer carries. 404 covers both "does not
 *  exist" and "not addressed to you"; the page says one thing for both. */
export function useInboxItem(uuid: string | undefined) {
  return useQuery({
    queryKey: INBOX_ITEM_KEY(uuid ?? ''),
    queryFn: async () => {
      const { data } = await apiClient.get<ShareItem>(`/inbox/items/${uuid}/`)
      return data
    },
    enabled: !!uuid,
    retry: false,
  })
}

export function useInboxSent({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: INBOX_SENT_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<SentDelivery[]>('/inbox/sent/')
      return data
    },
    enabled,
  })
}

interface AnswerShareVars {
  path: string
}

export interface AnswerShareResult {
  state: ShareState
  open_url?: string | null
}

/** Save or dismiss a share by posting its own path. Idempotent server side. */
export function useAnswerShare() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ path }: AnswerShareVars) => {
      const { data } = await apiClient.post<AnswerShareResult>(path, undefined, { baseURL: API_BASE })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INBOX_KEY })
      queryClient.invalidateQueries({ queryKey: INBOX_COUNT_KEY })
      queryClient.invalidateQueries({ queryKey: INBOX_SENT_KEY })
    },
  })
}

/** Sender cancels a pending delivery. */
export function useCancelDelivery() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (path: string) => {
      await apiClient.post(path, undefined, { baseURL: API_BASE })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INBOX_SENT_KEY })
      queryClient.invalidateQueries({ queryKey: INBOX_COUNT_KEY })
    },
  })
}

/** Sender resends a pending delivery. */
export function useResendDelivery() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (path: string) => {
      await apiClient.post(path, undefined, { baseURL: API_BASE })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INBOX_SENT_KEY })
    },
  })
}
